import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ApplicationService } from '../../services/ApplicationService';
import { JobService } from '../../services/JobService';
import { BusinessService } from '../../services/BusinessService';

const Updates = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [error, setError] = useState(null);
  const [withdrawingId, setWithdrawingId] = useState(null);

  useEffect(() => {
    if (currentUser) {
      fetchUserApplications();
    }
  }, [currentUser, activeFilter]);

  const fetchUserApplications = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch user applications
      const filterOptions = activeFilter !== 'all' ? { status: activeFilter } : {};
      const userApplications = await ApplicationService.getUserApplications(currentUser.uid, filterOptions);
      
      // Fetch job and business details for each application
      const enrichedApplications = await Promise.all(
        userApplications.map(async (application) => {
          try {
            // Fetch job details
            const jobData = await JobService.fetchJobById(application.jobId);
            
            // Fetch business details
            let businessData = null;
            if (application.businessId) {
              businessData = await BusinessService.fetchBusinessById(application.businessId);
            }
            
            return {
              ...application,
              jobData,
              businessData
            };
          } catch (error) {
            console.error('Error enriching application:', error);
            return application; // Return basic application data if enrichment fails
          }
        })
      );
      
      setApplications(enrichedApplications);
    } catch (error) {
      console.error('Error fetching applications:', error);
      // Only show error if it's not just an empty result
      if (error.message && !error.message.includes('No applications found')) {
        setError('Failed to load your applications. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleWithdrawApplication = async (applicationId, jobId) => {
    if (!window.confirm('Are you sure you want to withdraw this application? This action cannot be undone.')) {
      return;
    }

    try {
      setWithdrawingId(applicationId);
      const result = await ApplicationService.withdrawApplication(currentUser.uid, jobId);
      
      if (result.success) {
        // Update the local state to reflect the withdrawal
        setApplications(prev => 
          prev.map(app => 
            app.id === applicationId 
              ? { ...app, status: 'withdrawn' }
              : app
          )
        );
        alert('Application withdrawn successfully');
      } else {
        alert(result.message || 'Failed to withdraw application');
      }
    } catch (error) {
      console.error('Error withdrawing application:', error);
      alert('Failed to withdraw application. Please try again.');
    } finally {
      setWithdrawingId(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'accepted':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'withdrawn':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
          </svg>
        );
      case 'accepted':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        );
      case 'rejected':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
      case 'withdrawn':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
          </svg>
        );
      default:
        return null;
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  const filterOptions = [
    { key: 'all', label: 'All Applications', count: applications.length },
    { key: 'pending', label: 'Pending', count: applications.filter(app => app.status === 'pending').length },
    { key: 'accepted', label: 'Accepted', count: applications.filter(app => app.status === 'accepted').length },
    { key: 'rejected', label: 'Rejected', count: applications.filter(app => app.status === 'rejected').length }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF8C00]"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Applications</h1>
            <p className="text-gray-600 mt-1">Track your job applications and their status</p>
          </div>
          <button 
            onClick={fetchUserApplications}
            disabled={loading}
            className="mt-4 sm:mt-0 px-4 py-2 text-[#FF8C00] bg-[#FF8C00]/10 rounded-lg hover:bg-[#FF8C00]/20 transition-colors font-medium disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
        
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="ml-3 text-red-800">{error}</p>
            </div>
          </div>
        )}
        
        {/* Filter Tabs */}
        <div className="bg-white rounded-xl shadow-sm mb-6">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-wrap gap-2">
              {filterOptions.map(option => (
                <button 
                  key={option.key}
                  onClick={() => setActiveFilter(option.key)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    activeFilter === option.key 
                      ? 'bg-[#FF8C00] text-white shadow-sm' 
                      : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  {option.label}
                  {option.count > 0 && (
                    <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                      activeFilter === option.key 
                        ? 'bg-white/20 text-white' 
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {option.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Applications Grid */}
        {applications.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-left">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3 text-left">No Applications Found</h3>
            <p className="text-gray-600 mb-6 max-w-md text-left">
              {activeFilter === 'all' 
                ? "You haven't applied to any jobs yet. Start browsing jobs to find opportunities!" 
                : `No applications with ${activeFilter} status.`
              }
            </p>
            {activeFilter === 'all' && (
              <button
                onClick={() => navigate('/dashboard/jobs')}
                className="px-6 py-3 bg-[#FF8C00] text-white rounded-lg hover:bg-[#FF8C00]/90 transition-colors font-medium"
              >
                Browse Jobs
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-6">
            {applications.map(application => (
              <div 
                key={application.id} 
                className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100"
              >
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    {/* Company Logo */}
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden border border-gray-200">
                        {application.businessData?.logo ? (
                          <img 
                            src={application.businessData.logo} 
                            alt={application.businessData.businessName} 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-xl font-semibold text-gray-600">
                            {application.businessData?.businessName?.charAt(0) || 'B'}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Job Details */}
                    <div className="flex-grow">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-3">
                        <div className="text-left">
                          <h3 className="text-lg font-semibold text-gray-900 mb-1 text-left">
                            {application.jobData?.title || 'Job Title Unavailable'}
                          </h3>
                          <p className="text-gray-600 font-medium text-left">
                            {application.businessData?.businessName || 'Business Unavailable'}
                          </p>
                        </div>
                        <div className="flex items-start gap-3 mt-2 sm:mt-0">
                          {/* Status Badge - Made Bigger */}
                          <span className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-semibold border ${getStatusColor(application.status)}`}>
                            {getStatusIcon(application.status)}
                            <span className="ml-1.5 capitalize">{application.status}</span>
                          </span>
                          
                          {/* Withdraw Button */}
                          {application.status === 'pending' && (
                            <button
                              onClick={() => handleWithdrawApplication(application.id, application.jobId)}
                              disabled={withdrawingId === application.id}
                              className="px-3 py-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                            >
                              {withdrawingId === application.id ? 'Withdrawing...' : 'Withdraw'}
                            </button>
                          )}
                        </div>
                      </div>
                      
                                            {/* Job Meta Information */}
                      <div className="flex flex-wrap items-start gap-4 text-sm text-gray-600 mb-4 text-left">
                        {application.jobData?.hourlyRate && (
                          <div className="flex items-center gap-1">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                            </svg>
                            <span className="font-medium text-left">${application.jobData.hourlyRate}/hour</span>
                          </div>
                        )}
                        {application.jobData?.location && (
                          <div className="flex items-center gap-1">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span className="text-left">{application.jobData.location}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                           <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a1 1 0 011-1h6a1 1 0 011 1v4m0 4v6m4-10V9M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V9a2 2 0 00-2-2h-2M9 7h6" />
                           </svg>
                           <span className="text-left">Applied {formatDate(application.appliedAt)}</span>
                         </div>
                      </div>
                      
                      {/* Job Description */}
                      {application.jobData?.description && (
                        <p className="text-gray-700 text-sm leading-relaxed overflow-hidden text-left" style={{ 
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {application.jobData.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {applications.length > 0 && (
          <div className="mt-8 text-left">
            <p className="text-sm text-gray-600 text-left">
              Showing {applications.length} application{applications.length !== 1 ? 's' : ''}
              {activeFilter !== 'all' && ` with ${activeFilter} status`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Updates; 